<script setup>
import { onMounted, ref, computed } from 'vue'
import axios from '@/utils/axios'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { useLoading } from 'vue-loading-overlay'
import loaderData from '@/utils/loader-data'
import { useRouter } from 'vue-router'
import {
  describeDirectEditConfig,
  getDirectEditConfigTitle,
  normalizeDirectEditConfigs,
} from '@/utils/directDatabaseEdit'

const router = useRouter()
const $loading = useLoading(loaderData)
const route = useRoute('workflows-details-id')

const workflowData = ref({
  title: '',
  description: '',
  status: '',
  requireContact: 'optional',
  requireUser: 'optional',
  requireLicense: 'no',
  licenseNo: null,
  workflowCategoryId: null,
  type: '',
})

const workflowCategories = ref([])
const licenses = ref([])
const steps = ref([])
const taskCounts = ref({})

const toSafeString = value => {
  if (typeof value === 'string') return value
  if (value == null) return ''
  return String(value)
}

const toSafeArray = value => (Array.isArray(value) ? value : [])

const normalizeWorkflowStep = step => ({
  ...step,
  title: toSafeString(step?.title),
  description: toSafeString(step?.description),
  conditional_trigger_summary: toSafeString(step?.conditional_trigger_summary),
  forms: toSafeArray(step?.forms),
  documents: toSafeArray(step?.documents),
  document_templates: toSafeArray(step?.document_templates),
  smart_auto_field_groups: toSafeArray(step?.smart_auto_field_groups),
  approvers: toSafeArray(step?.approvers),
  tasks: toSafeArray(step?.tasks),
})

const normalizeWorkflowSteps = workflowSteps => {
  if (!Array.isArray(workflowSteps)) return []
  return workflowSteps
    .filter(step => step && typeof step === 'object' && !Array.isArray(step))
    .map(normalizeWorkflowStep)
}

const statusOptions = [
  { title: 'Draft', value: 'draft' },
  { title: 'Approved', value: 'approved' },
  { title: 'Suspended', value: 'suspended' },
]

const RequireContactOptions = [
  { title: 'Optional', value: 'optional' },
  { title: 'Required', value: 'required' },
  { title: 'Hidden', value: 'hidden' },
]

const RequireUserOptions = [
  { title: 'Optional', value: 'optional' },
  { title: 'Required', value: 'required' },
  { title: 'Hidden', value: 'hidden' },
]

const RequireLicenseOptions = [
  { title: 'Yes', value: 'yes' },
  { title: 'No', value: 'no' },
]

const typeOptions = [
  { title: 'Client', value: 'client' },
  { title: 'Operations', value: 'operations' },
  { title: 'Rep', value: 'rep' },
  { title: 'Branch Office', value: 'branch_office' },
]

const humanizeSnake = raw =>
  String(raw)
    .split('_')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')

const selectOptionTitle = (options, value, emptyLabel = '—') => {
  if (value == null || value === '') return emptyLabel
  const match = options.find(opt => String(opt.value) === String(value))
  if (match) return match.title
  return humanizeSnake(value)
}

const workflowTypeLabel = computed(() =>
  selectOptionTitle(typeOptions, workflowData.value?.type, 'Not set'),
)

const workflowRequireContactLabel = computed(() =>
  selectOptionTitle(RequireContactOptions, workflowData.value?.requireContact, 'Optional'),
)

const workflowRequireUserLabel = computed(() =>
  selectOptionTitle(RequireUserOptions, workflowData.value?.requireUser, 'Optional'),
)

const workflowRequireLicenseLabel = computed(() =>
  selectOptionTitle(RequireLicenseOptions, workflowData.value?.requireLicense, 'No'),
)

const workflowLicenseName = computed(() => {
  const no = workflowData.value?.licenseNo
  if (no == null || no === '') return '—'
  const match = licenses.value.find(l => String(l.value) === String(no))
  return match ? match.title : '—'
})

const workflowStatusLabel = computed(() =>
  selectOptionTitle(statusOptions, workflowData.value?.status, 'Draft'),
)

const workflowCategoryLabel = computed(() => {
  const id = workflowData.value?.workflowCategoryId
  if (id == null || id === '') return '—'
  const match = workflowCategories.value.find(c => String(c.value) === String(id))
  return match?.title ?? '—'
})

const extractTaskCount = workflowTasks => {
  if (Array.isArray(workflowTasks)) return workflowTasks.length
  if (Array.isArray(workflowTasks?.data)) return workflowTasks.data.length
  return 0
}

const fetchTaskCountForStep = async stepId => {
  if (!stepId || !route.params.id) return
  try {
    const response = await axios.get('/workflow_tasks', {
      params: {
        workflow_id: route.params.id,
        workflow_step_id: stepId,
      },
    })
    if (response.status === 'success') {
      taskCounts.value[stepId] = extractTaskCount(response.data.workflow_tasks)
    } else {
      taskCounts.value[stepId] = 0
    }
  } catch {
    taskCounts.value[stepId] = 0
  }
}

const fetchTaskCountsForSteps = async () => {
  if (!Array.isArray(steps.value) || steps.value.length === 0) return
  await Promise.all(
    steps.value.filter(step => step?.id).map(step => fetchTaskCountForStep(step.id)),
  )
}

const getStepTaskCount = step => taskCounts.value[step?.id] ?? 0

const selectedStepId = ref(null)

const selectedStep = computed(() => {
  if (selectedStepId.value == null) return null
  return steps.value.find(s => Number(s?.id) === Number(selectedStepId.value)) ?? null
})

const selectStep = step => {
  if (!step?.id) return
  selectedStepId.value = Number(step.id)
}

const showWorkflowOverview = () => {
  selectedStepId.value = null
}

const formatDriverEntityLabel = driverEntity =>
  String(driverEntity || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())

const stepDocumentCount = step =>
  (step?.documents?.length || 0) || (step?.document_templates?.length || 0)

const resolveStepTitleById = stepId => {
  if (stepId == null || stepId === '') return '—'
  const found = steps.value.find(s => Number(s.id) === Number(stepId))
  return found?.title || `Step #${stepId}`
}

const conditionalTriggerLabel = mode =>
  mode === 'on_step_completion' ? 'On step completion' : 'On selection'

const formatTaskTrigger = trigger => {
  if (trigger == null || trigger === '') return '—'
  return humanizeSnake(String(trigger))
}

const selectedStepDirectEditConfigs = computed(() => {
  if (!selectedStep.value) return []
  return normalizeDirectEditConfigs(selectedStep.value.direct_edit_config)
})

/** Child steps that are triggered from the selected step (same idea as “Conditional Step Triggers” in edit). */
const outgoingConditionalSteps = computed(() => {
  const sourceId = selectedStep.value?.id
  if (!sourceId || !Array.isArray(steps.value)) return []
  return steps.value.filter(
    step =>
      step?.is_conditional &&
      Number(step.conditional_parent_step_id) === Number(sourceId),
  )
})

const getworkflowData = () => {
  const id = route.params.id
  const loader = $loading.show()
  const url = `/workflows/${id}`
  axios
    .get(url)
    .then(response => {
      if (response.status == 'success') {
        workflowData.value = response.data.workflow
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        })
      }
    })
    .catch(() => {
      loader.hide()
    })
    .finally(() => {
      loader.hide()
    })
}

const getWorkflowCategories = () => {
  axios
    .get('/workflow-categories?all=true&status=active')
    .then(response => {
      if (response.status === 'success') {
        workflowCategories.value = (response.data || []).map(category => ({
          title: category.name,
          value: category.id,
        }))
      }
    })
    .catch(() => {})
}

const getLicenses = () => {
  axios
    .get('/licenses?per_page=100')
    .then(response => {
      if (response.status === 'success') {
        licenses.value = (response.data.licenses?.data || []).map(license => ({
          title: license.name,
          value: license.number,
        }))
      }
    })
    .catch(() => {})
}

const getWorkflowStepsData = () => {
  const loader = $loading.show()
  axios
    .get(`/workflow_steps?workflow_id=${route.params.id}`)
    .then(response => {
      if (response.status == 'success') {
        steps.value = normalizeWorkflowSteps(response.data.workflow_steps)
        fetchTaskCountsForSteps()
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        })
      }
    })
    .catch(() => {
      loader.hide()
    })
    .finally(() => {
      loader.hide()
    })
}

onMounted(() => {
  getworkflowData()
  getWorkflowCategories()
  getLicenses()
  getWorkflowStepsData()
})

const goBack = () => {
  router.push({ name: 'workflows-manage' })
}

const goToEdit = () => {
  const id = workflowData.value?.id ?? route.params.id
  if (!id) return
  router.push({ name: 'workflows-id', params: { id: String(id) } })
}

definePage({ meta: { requiresAuth: true } })
</script>

<template>
  <div class="workflow-editor-page">
    <div class="workflow-grid">
      <VCard class="workflow-tree-card" rounded="lg">
        <VCardText class="pa-0">
          <div class="tree-header">
            <div class="tree-header-top">
              <div class="tree-header-left">
                <div class="tree-title-row d-flex align-center gap-2 flex-wrap">
                  <h2 class="tree-title">{{ workflowData.title || 'Workflow' }}</h2>
                  <VChip
                    size="small"
                    :color="
                      workflowData.status === 'approved'
                        ? 'success'
                        : workflowData.status === 'suspended'
                          ? 'warning'
                          : 'secondary'
                    "
                    variant="tonal"
                    class="workflow-status-chip"
                  >
                    {{ workflowStatusLabel }}
                  </VChip>
                </div>
              </div>

              <div class="tree-header-actions d-flex align-center gap-2 flex-shrink-0 flex-wrap justify-end">
                <VBtn
                  v-if="selectedStepId != null"
                  color="primary"
                  class="flex-shrink-0"
                  @click="showWorkflowOverview"
                >
                  <VIcon icon="tabler-info-circle" start />
                  Workflow overview
                </VBtn>
                <VBtn color="primary" class="flex-shrink-0" @click="goToEdit">
                  <VIcon icon="tabler-edit" start />
                  Edit workflow
                </VBtn>
                <VBtn color="primary" variant="outlined" class="flex-shrink-0" @click="goBack">
                  <VIcon icon="tabler-arrow-left" start />
                  Back
                </VBtn>
              </div>
            </div>

            <div class="tree-meta tree-meta--subhead">
              <span>Type: <span class="tree-meta-value">{{ workflowTypeLabel }}</span></span>
              <span class="tree-meta-sep" aria-hidden="true">|</span>
              <span>Contact: <span class="tree-meta-value">{{ workflowRequireContactLabel }}</span></span>
              <span class="tree-meta-sep" aria-hidden="true">|</span>
              <span>User: <span class="tree-meta-value">{{ workflowRequireUserLabel }}</span></span>
              <span class="tree-meta-sep" aria-hidden="true">|</span>
              <span>License: <span class="tree-meta-value">{{ workflowRequireLicenseLabel }}</span></span>
            </div>
          </div>

          <div class="tree-body">
            <div class="start-pill">Start</div>

            <template v-if="steps.length">
              <div v-for="(step, index) in steps" :key="step.id ?? index" class="step-wrap">
                <div class="connector" />

                <VCard
                  class="step-card step-card--clickable"
                  :class="{ 'step-card--selected': selectedStepId != null && Number(selectedStepId) === Number(step.id) }"
                  variant="outlined"
                  role="button"
                  tabindex="0"
                  @click="selectStep(step)"
                  @keydown.enter.prevent="selectStep(step)"
                  @keydown.space.prevent="selectStep(step)"
                >
                  <VCardText class="pa-3">
                    <div class="step-header step-header--readonly">
                      <div class="step-title-wrap d-flex align-center gap-2 min-w-0">
                        <div class="step-name text-truncate">
                          {{ step.title || 'Untitled Step' }}
                        </div>
                        <VChip
                          v-if="step.optional"
                          size="x-small"
                          color="primary"
                          variant="tonal"
                          class="flex-shrink-0"
                        >
                          Optional
                        </VChip>
                      </div>
                    </div>

                    <div class="step-sub mt-2">
                      <div class="step-sub-line">
                        <span>{{ step.forms?.length ? 'Forms attached' : 'No forms' }}</span>
                        <span class="mx-2">•</span>
                        <span>{{ stepDocumentCount(step) ? 'Documents attached' : 'No documents' }}</span>
                      </div>
                      <div class="step-sub-line">
                        <span>Tasks: {{ getStepTaskCount(step) }}</span>
                        <span class="mx-2">•</span>
                        <span>{{
                          step.direct_edit_config ? 'Direct Database Edit: Yes' : 'Direct Database Edit: No'
                        }}</span>
                      </div>
                    </div>
                  </VCardText>
                </VCard>
              </div>
            </template>

            <template v-else>
              <div class="empty-tree">
                <p class="mb-0 text-medium-emphasis">No steps configured for this workflow.</p>
              </div>
            </template>
          </div>
        </VCardText>
      </VCard>

      <aside class="workflow-sidebar-column" aria-label="Workflow details">
        <!-- Workflow-level summary (no step selected) -->
        <VCard v-if="!selectedStep" class="workflow-info-card" rounded="lg">
          <VCardText class="pa-5 workflow-detail-panel-body">
            <div class="workflow-detail-panel-head mb-4">
              <div class="workflow-info-head mb-2">
                <h3 class="workflow-info-title mb-0">Workflow Information</h3>
              </div>

              <p class="text-body-2 text-medium-emphasis mb-0">
                Select a step in the diagram to view its forms, documents, approvers, and other settings.
              </p>
            </div>

            <div class="readonly-info-stack workflow-detail-scroll">
              <div class="readonly-field">
                <div class="readonly-label">Title</div>
                <div class="readonly-value">{{ workflowData.title || '—' }}</div>
              </div>
              <div class="readonly-field">
                <div class="readonly-label">Status</div>
                <div class="readonly-value">
                  <VChip
                    size="small"
                    :color="
                      workflowData.status === 'approved'
                        ? 'success'
                        : workflowData.status === 'suspended'
                          ? 'warning'
                          : 'secondary'
                    "
                    variant="tonal"
                  >
                    {{ workflowStatusLabel }}
                  </VChip>
                </div>
              </div>
              <div class="readonly-field">
                <div class="readonly-label">Type</div>
                <div class="readonly-value">{{ workflowTypeLabel }}</div>
              </div>
              <div class="readonly-field">
                <div class="readonly-label">Require contact</div>
                <div class="readonly-value">{{ workflowRequireContactLabel }}</div>
              </div>
              <div class="readonly-field">
                <div class="readonly-label">Require user</div>
                <div class="readonly-value">{{ workflowRequireUserLabel }}</div>
              </div>
              <div class="readonly-field">
                <div class="readonly-label">Require license</div>
                <div class="readonly-value">{{ workflowRequireLicenseLabel }}</div>
              </div>
              <div v-if="workflowData.requireLicense === 'yes'" class="readonly-field">
                <div class="readonly-label">License</div>
                <div class="readonly-value">{{ workflowLicenseName }}</div>
              </div>
              <div class="readonly-field">
                <div class="readonly-label">Category</div>
                <div class="readonly-value">{{ workflowCategoryLabel }}</div>
              </div>
              <div class="readonly-field">
                <div class="readonly-label">Description</div>
                <div class="readonly-value readonly-value--multiline">
                  {{ workflowData.description || '—' }}
                </div>
              </div>
            </div>
          </VCardText>
        </VCard>

        <!-- Step detail (after clicking a step) -->
        <VCard v-else class="workflow-info-card workflow-step-detail-card" rounded="lg">
          <VCardText class="pa-5 workflow-detail-panel-body">
            <div class="workflow-detail-panel-head mb-4">
              <div class="workflow-info-head workflow-info-head--row mb-0">
                <VBtn
                  icon
                  variant="text"
                  density="comfortable"
                  class="workflow-step-back"
                  aria-label="Back to workflow overview"
                  @click="showWorkflowOverview"
                >
                  <VIcon icon="tabler-arrow-left" size="22" />
                </VBtn>
                <h3 class="workflow-info-title mb-0">Step details</h3>
              </div>
            </div>

            <div class="readonly-info-stack workflow-detail-scroll step-detail-scroll">
              <!-- Same order as WorkflowStepConfiguratorForm: Title → Parent → Optional / Allow editing → Description -->
              <div class="readonly-field">
                <div class="readonly-label">Title</div>
                <div class="readonly-value">{{ selectedStep.title || '—' }}</div>
              </div>

              <div v-if="selectedStep.parent_step_id" class="readonly-field">
                <div class="readonly-label">Parent step</div>
                <div class="readonly-value">{{ resolveStepTitleById(selectedStep.parent_step_id) }}</div>
              </div>

              <div class="readonly-field">
                <div class="readonly-label">Optional</div>
                <div class="readonly-value">{{ selectedStep.optional ? 'Yes' : 'No' }}</div>
              </div>

              <div class="readonly-field">
                <div class="readonly-label">Allow editing after complete</div>
                <div class="readonly-value">{{ selectedStep.allow_editing_after_complete ? 'Yes' : 'No' }}</div>
              </div>

              <div v-if="selectedStep.is_conditional" class="readonly-field d-flex flex-wrap align-center gap-2">
                <VChip size="small" color="secondary" variant="tonal">Runs as conditional branch</VChip>
              </div>

              <div class="readonly-field">
                <div class="readonly-label">Description</div>
                <div class="readonly-value readonly-value--multiline">
                  {{ selectedStep.description || '—' }}
                </div>
              </div>

              <!-- Approvals (approvers + approval routing — same section as edit “Approvals”) -->
              <div class="step-detail-section">
                <div class="readonly-label mb-2 step-section-title">Approvals</div>
                <template v-if="selectedStep.approvers?.length">
                  <div
                    v-for="(approver, ai) in selectedStep.approvers"
                    :key="`ap-${ai}`"
                    class="theme-bg rounded-lg pa-3 mb-3"
                  >
                    <div class="d-flex align-center gap-2 flex-wrap">
                      <VIcon size="20" icon="tabler-user-check" />
                      <strong>{{ approver.first_name }} {{ approver.last_name }}</strong>
                      <VChip v-if="approver.role_name" size="small" variant="tonal">{{ approver.role_name }}</VChip>
                    </div>
                  </div>
                </template>
                <p v-else class="text-body-2 text-medium-emphasis mb-3">No approvers.</p>

                <div
                  v-if="selectedStep.approval_dependency_mappings && selectedStep.approval_dependency_mappings.length"
                  class="mt-2"
                >
                  <div class="readonly-label mb-2">Approval routing</div>
                  <div class="theme-bg rounded-lg pa-3 mb-0">
                    <ul class="detail-bullet-list mb-0">
                      <li
                        v-for="(row, ri) in selectedStep.approval_dependency_mappings"
                        :key="`adm-${ri}`"
                        class="text-body-2"
                      >
                        <template v-if="typeof row === 'object' && row !== null">
                          <span v-if="row.target_step_id">{{ resolveStepTitleById(row.target_step_id) }}</span>
                          <span v-if="row.trigger_mode"> · {{ row.trigger_mode }}</span>
                          <span v-if="row.summary || row.note"> — {{ row.summary || row.note }}</span>
                        </template>
                        <template v-else>{{ row }}</template>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Forms -->
              <div class="step-detail-section">
                <div class="readonly-label mb-2 step-section-title">Forms</div>
                <template v-if="selectedStep.forms?.length">
                  <div
                    v-for="(form, fi) in selectedStep.forms"
                    :key="`form-${fi}-${form.id ?? fi}`"
                    class="theme-bg rounded-lg pa-3 mb-3"
                  >
                    <div class="d-flex align-center gap-2 mb-1">
                      <VIcon size="20" icon="tabler-forms" />
                      <strong>{{ form.title || form.name }}</strong>
                    </div>
                    <p v-if="form.description" class="text-body-2 text-medium-emphasis mb-3">{{ form.description }}</p>

                    <template v-if="form.form_fields?.length">
                      <div
                        v-for="(field, fidx) in form.form_fields"
                        :key="`ff-${fidx}-${field.id}`"
                        class="field-detail-block border rounded-lg pa-3 mb-2"
                      >
                        <div class="d-flex flex-wrap align-center gap-2">
                          <span class="font-weight-medium">{{ field.name }}</span>
                          <VChip size="x-small" variant="tonal">{{ field.type_name || field.type }}</VChip>
                          <VChip v-if="field.is_required" size="x-small" color="error" variant="tonal">Required</VChip>
                          <VChip v-if="field.is_lookup_field" size="x-small" color="secondary" variant="tonal">
                            Lookup
                          </VChip>
                          <VChip v-if="field.act_as_parent" size="x-small" variant="outlined">Parent picklist</VChip>
                        </div>
                      </div>
                    </template>
                    <p v-else class="text-caption text-medium-emphasis mb-0">No fields on this form.</p>
                  </div>
                </template>
                <p v-else class="text-body-2 text-medium-emphasis mb-0">No forms attached.</p>
              </div>

              <!-- Document templates -->
              <div class="step-detail-section">
                <div class="readonly-label mb-2 step-section-title">Document templates</div>
                <template v-if="selectedStep.document_templates?.length">
                  <div
                    v-for="(template, ti) in selectedStep.document_templates"
                    :key="`tpl-${ti}`"
                    class="theme-bg rounded-lg pa-3 mb-3"
                  >
                    <div class="d-flex align-center gap-2 mb-1 flex-wrap">
                      <VIcon size="20" icon="tabler-file-text" />
                      <strong>{{ template.name }}</strong>
                    </div>
                    <div v-if="template.documents?.length" class="mb-0">
                      <div
                        v-for="(doc, di) in template.documents"
                        :key="`doc-${ti}-${di}-${doc.id}`"
                        class="border rounded-lg pa-2 mb-2"
                      >
                        <div class="font-weight-medium text-body-2">{{ doc.name }}</div>
                        <div class="text-caption text-medium-emphasis">
                          {{ doc.file_type }}
                          <span v-if="doc.mappings?.length"> · {{ doc.mappings.length }} field mapping(s)</span>
                        </div>
                      </div>
                    </div>
                    <p v-else class="text-caption text-medium-emphasis mb-0">
                      {{ template.documents_count || 0 }} document(s)
                    </p>
                  </div>
                </template>
                <p v-else class="text-body-2 text-medium-emphasis mb-0">No document templates.</p>
              </div>

              <!-- Tasks -->
              <div class="step-detail-section">
                <div class="readonly-label mb-2 step-section-title">Tasks</div>
                <template v-if="selectedStep.tasks?.length">
                  <div
                    v-for="(task, tidx) in selectedStep.tasks"
                    :key="`task-${task.id ?? tidx}`"
                    class="theme-bg rounded-lg pa-3 mb-3"
                  >
                    <div class="d-flex align-start justify-space-between gap-2 flex-wrap mb-1">
                      <strong class="text-body-1">{{ task.title || `Task ${tidx + 1}` }}</strong>
                      <VChip size="x-small" variant="tonal">{{ task.status || 'draft' }}</VChip>
                    </div>
                    <p v-if="task.description" class="text-body-2 text-medium-emphasis mb-2">{{ task.description }}</p>
                    <div class="text-caption text-medium-emphasis mb-2">
                      Trigger: {{ formatTaskTrigger(task.trigger) }}
                    </div>
                    <div v-if="task.assignments?.length" class="text-body-2">
                      <span class="text-medium-emphasis">Assignees:</span>
                      <ul class="mt-1 mb-0 pl-4">
                        <li v-for="(asg, ax) in task.assignments" :key="`asg-${ax}`">
                          <template v-if="asg.user">
                            {{ asg.user.first_name }} {{ asg.user.last_name }}
                          </template>
                          <template v-else>Assigned user</template>
                        </li>
                      </ul>
                    </div>
                    <div v-else class="text-caption text-medium-emphasis">No default assignees configured.</div>
                  </div>
                </template>
                <p v-else class="text-body-2 text-medium-emphasis mb-0">No workflow tasks linked to this step.</p>
              </div>

              <!-- Direct database edit -->
              <div class="step-detail-section">
                <div class="readonly-label mb-2 step-section-title">Direct database edit</div>
                <template v-if="selectedStepDirectEditConfigs.length">
                  <div
                    v-for="(cfg, didx) in selectedStepDirectEditConfigs"
                    :key="cfg.item_key || didx"
                    class="theme-bg rounded-lg pa-3 mb-3"
                  >
                    <div class="font-weight-medium mb-1">{{ getDirectEditConfigTitle(cfg, didx) }}</div>
                    <div class="text-body-2 text-medium-emphasis mb-0">{{ describeDirectEditConfig(cfg) }}</div>
                  </div>
                </template>
                <p v-else class="text-body-2 text-medium-emphasis mb-0">Not configured for this step.</p>
              </div>

              <!-- Conditional Step Triggers — same position as WorkflowStepConfiguratorForm (after Direct database edit) -->
              <div class="step-detail-section">
                <div class="readonly-label mb-2 step-section-title">Conditional Step Triggers</div>
                <template v-if="outgoingConditionalSteps.length">
                  <div
                    v-for="child in outgoingConditionalSteps"
                    :key="`out-${child.id}`"
                    class="theme-bg rounded-lg pa-3 mb-3"
                  >
                    <div class="font-weight-medium mb-1">{{ child.title || `Step #${child.id}` }}</div>
                    <div class="text-caption text-medium-emphasis mb-2">
                      {{ conditionalTriggerLabel(child.conditional_trigger_mode) }}
                    </div>
                    <p class="text-body-2 text-medium-emphasis mb-0">
                      {{ child.conditional_trigger_summary || '—' }}
                    </p>
                  </div>
                </template>
                <p v-else class="text-body-2 text-medium-emphasis mb-0">
                  No conditional branches are configured from this step.
                </p>
              </div>

              <!-- Read-only: how this step is reached when it is a conditional child (not a separate editor block) -->
              <div v-if="selectedStep.is_conditional" class="step-detail-section">
                <div class="readonly-label mb-2 step-section-title">Incoming conditional rules</div>
                <div class="readonly-field">
                  <div class="readonly-label">Parent step (conditions from)</div>
                  <div class="readonly-value">{{ resolveStepTitleById(selectedStep.conditional_parent_step_id) }}</div>
                </div>
                <div class="readonly-field">
                  <div class="readonly-label">When to evaluate</div>
                  <div class="readonly-value">{{ conditionalTriggerLabel(selectedStep.conditional_trigger_mode) }}</div>
                </div>
                <div v-if="selectedStep.conditional_option_value != null && selectedStep.conditional_option_value !== ''" class="readonly-field">
                  <div class="readonly-label">Option value (legacy rule)</div>
                  <div class="readonly-value">{{ selectedStep.conditional_option_value }}</div>
                </div>
                <div class="readonly-field">
                  <div class="readonly-label">Summary</div>
                  <div class="readonly-value readonly-value--multiline">
                    {{
                      selectedStep.conditional_trigger_summary ||
                        'Triggered when conditions on the parent step are met.'
                    }}
                  </div>
                </div>
              </div>

              <!-- Smart auto field groups (extra API detail — not a separate editor section) -->
              <div v-if="selectedStep.smart_auto_field_groups?.length" class="step-detail-section">
                <div class="readonly-label mb-2 step-section-title">Smart auto field groups</div>
                <div
                  v-for="(group, gi) in selectedStep.smart_auto_field_groups"
                  :key="`sg-${gi}`"
                  class="theme-bg rounded-lg pa-3 mb-3"
                >
                  <div class="d-flex align-center gap-2 mb-2">
                    <VIcon size="20" icon="tabler-sparkles" />
                    <strong>{{ group.name }}</strong>
                  </div>
                  <div class="d-flex flex-wrap gap-1 mb-2">
                    <VChip size="small">{{ formatDriverEntityLabel(group.driver_entity) }}</VChip>
                    <VChip size="small">{{ group.mapped_fields?.length || group.fields_count || 0 }} mapped fields</VChip>
                  </div>
                  <div
                    v-for="(field, sfi) in group.mapped_fields || []"
                    :key="`sf-${gi}-${sfi}`"
                    class="border rounded pa-2 mb-2 text-body-2"
                  >
                    <div class="font-weight-medium">{{ field.label || 'Untitled field' }}</div>
                    <div class="text-medium-emphasis">Table: {{ field.source_table || '—' }}</div>
                    <div class="text-medium-emphasis">Column: {{ field.source_column || '—' }}</div>
                  </div>
                </div>
              </div>
            </div>
          </VCardText>
        </VCard>
      </aside>
    </div>
  </div>
</template>

<style scoped>
.workflow-editor-page {
  width: 100%;
}

.workflow-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(340px, 440px);
  gap: 18px;
  align-items: stretch;
}

.workflow-tree-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  min-height: calc(100vh - 180px);
}

.workflow-sidebar-column {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  min-height: calc(100vh - 180px);
}

.workflow-info-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  position: sticky;
  top: 84px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 180px);
  max-height: calc(100vh - 120px);
}

.workflow-detail-panel-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0 !important;
  overflow: hidden;
}

.workflow-detail-panel-head {
  flex-shrink: 0;
}

.workflow-info-head {
  min-width: 0;
}

.workflow-info-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.35;
}

.workflow-info-head--row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.workflow-step-back {
  flex-shrink: 0;
}

.workflow-detail-scroll,
.step-detail-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  -webkit-overflow-scrolling: touch;
}

.step-detail-section {
  padding-top: 4px;
}

.detail-bullet-list {
  padding-inline-start: 1.15rem;
  margin: 0;
}

.detail-bullet-list li + li {
  margin-top: 4px;
}

.field-detail-block {
  background: rgba(var(--v-theme-surface), 0.35);
}

.theme-bg {
  background-color: rgba(var(--v-theme-on-background), var(--v-hover-opacity)) !important;
}

.step-card--clickable {
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.step-card--clickable:hover {
  border-color: rgba(139, 20, 60, 0.35);
}

.step-card--clickable.step-card--selected {
  background-color: rgba(139, 20, 60, 0.055);
  border-color: #8b143c !important;
}

.readonly-info-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.readonly-field {
  min-width: 0;
}

.readonly-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(var(--v-theme-on-surface), 0.55);
  margin-bottom: 4px;
}

.readonly-label.step-section-title {
  font-size: 1rem;
  font-weight: 700;
  text-transform: none;
  letter-spacing: normal;
  color: rgb(var(--v-theme-on-surface));
  margin-bottom: 12px;
}

.readonly-value {
  font-size: 0.9375rem;
  color: rgb(var(--v-theme-on-surface));
  font-weight: 500;
  word-break: break-word;
}

.readonly-value--multiline {
  white-space: pre-wrap;
  font-weight: 400;
}

.tree-header {
  padding: 18px 20px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}

.tree-header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.tree-header-left {
  min-width: 0;
  flex: 1;
}

.tree-title-row {
  margin-bottom: 0;
}

.tree-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.25;
}

.workflow-status-chip {
  flex-shrink: 0;
}

.tree-meta {
  color: rgba(var(--v-theme-on-surface), 0.62);
  font-size: 0.9375rem;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
}

.tree-meta--subhead {
  flex-wrap: nowrap;
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  padding-bottom: 2px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

.tree-meta-value {
  color: rgb(var(--v-theme-on-surface));
  font-weight: 600;
}

.tree-meta-sep {
  color: rgba(var(--v-theme-on-surface), 0.35);
  user-select: none;
}

@media (max-width: 600px) {
  .tree-title {
    font-size: 1.45rem;
  }
}

.tree-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.start-pill {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.18);
  border-radius: 999px;
  padding: 8px 24px;
  font-weight: 600;
  background: #fff;
}

.step-wrap {
  width: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.connector {
  width: 2px;
  height: 44px;
  background: rgba(var(--v-theme-on-surface), 0.2);
  position: relative;
  margin-bottom: 4px;
}

.connector::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -6px;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 7px solid rgba(var(--v-theme-on-surface), 0.35);
}

.step-card {
  width: 100%;
  border-radius: 10px;
}

.step-name {
  font-weight: 600;
  line-height: 1.2;
  min-width: 0;
  font-size: 1.1rem;
  color: #8b143c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  max-width: 100%;
}

.step-title-wrap {
  max-width: 100%;
}

.step-header {
  position: relative;
  min-height: 34px;
  padding-right: 0;
  display: flex;
  align-items: center;
}

.step-header--readonly {
  padding-right: 0;
}

.step-sub {
  font-size: 0.82rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.step-sub-line + .step-sub-line {
  margin-top: 2px;
}

.empty-tree {
  margin-top: 36px;
  text-align: center;
}

@media (max-width: 1280px) {
  .workflow-grid {
    grid-template-columns: 1fr;
  }

  .workflow-sidebar-column {
    min-height: auto;
  }

  .workflow-info-card {
    position: static;
    min-height: auto;
    max-height: none;
  }

  .workflow-detail-scroll,
  .step-detail-scroll {
    flex: none;
    max-height: min(65vh, 520px);
    overflow-y: auto;
  }

  .step-wrap {
    width: 100%;
  }
}
</style>
